import { RegisterOptions } from "react-hook-form";
import { IFormFieldErrorMessages } from "../components/ui/interface";

export type FormFieldValidation = RegisterOptions<any, string> | undefined;
type IUseFormValidationsReturn = {
  [key in
    | "forName"
    | "forRating"
    | "forRatingScore"
    | "forAlphaNumeric"
    | "forStaffName"
    | "forEquipmentName"
    | "forEmail"
    | "forOnlyBarcode"
    | "forMobile"
    | "forOnlyNumber"
    | "forLatitude"
    | "forLongitude"
    | "forOnlyAlphabet"
    | "forOnlyAlphabetWithoutDot"
    | "forAddressLine"
    | "forClinicAddressLine"
    | "forNumericWithDecimal"
    | "forIndianPanNumber"
    | "forAlphaNumericWithoutSpace"
    | "forAllAcceptWithOutStartWithDotAndSpace"
    | "forAlphaNumericWithoutDot"
    | "forOnlyNumberWithoutZero"
    | "forAlphaNumericWithGST"
    | "forMsmeValidationValidation"
    | "forTaxRegex"
    | "forOnlyPercentageAndNumberRegex"
    | "forOnlyAlphabetWithAlfhanumeric"
    | "forOnlyStrictAlphabets"
    | "forOnlyAlphabetAndSpaces"
    | "forOnlyNumbers"
    | "foruserPersonaCreation"
    | "forOnlyNumbersWithDecimal"
    | "forOnlyAlphabetWithoutDotSpace"
    | "forNumericWithDecimalAndNotStartFromZero"
    | "forUrl"
    | "forGmbPlaceId"
    | "forLocationPath"
    | "forExtensionDays"
    | "forAliasCode"]: {
    validations: FormFieldValidation;
    errors: IFormFieldErrorMessages;
  };
};
const ONLY_NUMBER_REGEX = /^\d{1,10}$/;
const GS1_BARCODE_REGEX = /^\d+$/;
const MSME_REGEX = /^UDYAM-[A-Z]{2}-\d{2}-\d{7}$/;
const ONLY_NUMBER_REGEX_WITHOUT_ZERO = /^(?!0+$)\d{1,10}$/;
const ONLY_ALPHABETS = /^(?![ .])[a-zA-Z\s]+$/;
const NO_DOT_OR_SPACE_START = /^(?![ .]).*$/;
const ADDRESS_REGEX = /^(?![\s.]).*$/;
const ONLY_ALPHABETS_WITH_OUT_DOT = /^[a-zA-Z]+(?:[ .][a-zA-Z]+)*$/;
const ONLY_ALPHABETS_WITHOUT_DOT_NEW = /^(?![ .])[a-zA-Z](?:[ .]?[a-zA-Z0-9]+)*[.]?$/;
const ONLY_STRICT_ALPHABETS_AND_NUMBER = /^[A-Za-z]+(?: [A-Za-z]+)*$/i;
const NAME_REGEX = /^[a-zA-Z\s-_0-9]+$/;
const ALPHA_NUMERIC_REGEX = /^[a-zA-Z0-9 ]+$/;
const GST_REGEX =
  /^([0][1-9]|[1-2][0-9]|[3][0-7])([a-zA-Z]{5}[0-9]{4}[a-zA-Z]{1}[1-9a-zA-Z]{1}[zZ]{1}[0-9a-zA-Z]{1})+$/;

const ALPHA_NUMERIC_WITHOUT_DOT = /^[a-zA-Z0-9 !@#$%^&*()_+\-=[\]{};':"\\|,<>/?]+$/;
const ALPHA_NUMERIC_WITHOUT_SPACE = /^[a-zA-Z0-9]+$/;
const RATING_REGEX = /^[0-9.]+$/;
const RATING_SCORE_REGEX = /^(5(\.0{1,2})?|[0-4](\.\d{1,2})?)$/;
const STAFF_NAME_REGEX = /^[a-zA-Z\s-.0-9]+$/;
const EQUIPMENT_NAME_REGEX = /^[a-zA-Z\s-_0-9]+$/;
const LAT_REGEX = /^[-+]?([1-8]?\d(\.\d+)?|90(\.0+)?)$/;
const LONG_REGEX = /^[-+]?([1-9]?\d(\.\d+)?|1[0-7]\d(\.\d+)?|180(\.0+)?)$/;
const MOBILE_REGEX = /^[6-9]\d{9}$/i;
const EMAIL_REGEX = /^[A-Za-z0-9._%+-]{1,64}@[A-Za-z0-9.-]{1,253}\.[A-Za-z]{2,}$/;
const ADDRESS_LINE_REGEX = /^[a-zA-Z0-9, ]+$/i;
const CLINIC_ADDRESS_REGEX = /^(?![\s.])(?=.*[a-zA-Z0-9]).+$/;
const NUMERIC_WITH_DECIMAL = /^\d+(\.\d{1,2})?$/i;
const INDIAN_PAN_NUMBER_REGEX = /[A-Z]{5}[0-9]{4}[A-Z]{1}/i;
const TAX_REGEX = /^(?:\d{1,2}(?:\.\d{1,2})?|100(?:\.0{1,2})?)$/i;
const PERCENTAGE_REGEX = /^(?:(\d{1,7}(?:,\d{3})*(?:\.\d{1,2})?)|(\d{1,2}(?:\.\d{1,2})?|100(?:\.0{1,2})?))$/i;
const ONLY_ALPHABETS_AND_SPACES = /^[a-zA-Z\s]+$/;
const ONLY_NUMBER_REGEX_NEW = /^\d+$/;
const ONLY_NUMBER_WITH_DECIMAL = /^\d*\.?\d+$/;
const USER_PERSONA_CREATION_REGEX = /^[a-z0-9_]+$/;
const NUMERIC_WITH_DECIMAL_AND_NOT_START_WITH_ZERO = /^[1-9]\d*(\.\d{1,2})?$/;
const URL_REGEX = /^(https?:\/\/)([\da-z.-]+)\.([a-z.]{2,6})([/\w .\-~:?#[\]@!$&'()*+,;=%]*)*\/?$/i;
const GMB_PLACE_ID_REGEX = /^[A-Za-z0-9_-]+$/;
const LOCATION_PATH_REGEX = /^(?![.,])[a-zA-Z0-9,\-_/:.]+$/;
const ALIAS_CODE_REGEX = /^[A-Z]{3,5}$/;
// Whole days from 1 to 366 (a leap year), no leading zeros, decimals or signs
const EXTENSION_DAYS_REGEX = /^(?:[1-9]|[1-9]\d|[12]\d{2}|3[0-5]\d|36[0-6])$/;
export const ALIAS_CODE_SANITIZE_REGEX = /[^a-zA-Z]/g;

const useFormValidations = (): IUseFormValidationsReturn => {
  // Address Fields
  const addressLineValidations: FormFieldValidation = {
    pattern: ADDRESS_LINE_REGEX,
  };
  const addressLineValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Cannot contain special chars",
  };

  // Clinic Setup Address — special characters allowed, but must contain at
  // least one letter/number and must not start with a space or dot
  const clinicAddressLineValidations: FormFieldValidation = {
    pattern: CLINIC_ADDRESS_REGEX,
  };
  const clinicAddressLineValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Enter a valid address",
  };

  const forOnlyAlphabetWithoutDotValidationNew: FormFieldValidation = { pattern: ONLY_ALPHABETS_WITHOUT_DOT_NEW };
  const forOnlyAlphabetWithoutDotErrorsNew: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format",
  };

  // Only Numbers
  const onlyNumbersValidationsNew: FormFieldValidation = {
    pattern: {
      value: ONLY_NUMBER_REGEX_NEW,
      message: "Only numbers are allowed",
    },
  };
  const onlyNumbersValidationsWithDecimal: FormFieldValidation = {
    pattern: {
      value: ONLY_NUMBER_WITH_DECIMAL,
      message: "Invalid Value",
    },
  };

  const onlyNumbersValidationsWithDecimalError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid value",
  };
  const onlyNumbersValidationErrorsNew: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numbers are allowed",
  };

  const onlyAlphabetsAndSpacesValidations: FormFieldValidation = {
    pattern: ONLY_ALPHABETS_AND_SPACES,
  };
  const onlyAlphabetsAndSpacesValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only alphabets are allowed",
  };

  const onlyNumbersValidations: FormFieldValidation = {
    pattern: ONLY_NUMBER_REGEX,
  };
  const onlyStrictAlphabetsAndNumberValidations: FormFieldValidation = {
    pattern: ONLY_STRICT_ALPHABETS_AND_NUMBER,
  };
  const onlyStrictAlphabetsAndNumberErrors: IFormFieldErrorMessages = {
    message: "Only Alphbets",
    type: "pattern",
  };
  const onlyNumbersValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numbers are allowed",
  };
  const onlyBarcodeValidations: FormFieldValidation = {
    pattern: GS1_BARCODE_REGEX,
  };
  const onlyBarcodeValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numbers are allowed",
  };
  const onlyNumbersValidationsWithoutZero: FormFieldValidation = {
    pattern: ONLY_NUMBER_REGEX_WITHOUT_ZERO,
  };
  const onlyNumbersValidationsWithoutZeroErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numbers and greater than 0 ",
  };
  const onlyUserPerSona: FormFieldValidation = {
    pattern: USER_PERSONA_CREATION_REGEX,
  };
  const onlyUserPerSonaError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Must be lowercase with no spaces (use _ instead of space)",
  };

  const onlyAlphaNumericWithoutSpaceValidations: FormFieldValidation = {
    pattern: ALPHA_NUMERIC_WITHOUT_SPACE,
  };
  const onlyAlphaNumericWithoutSpaceErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Should not contain any space or special characters",
  };
  const forAllAcceptWithOutStartWithDotAndSpaceValidation: FormFieldValidation = {
    pattern: NO_DOT_OR_SPACE_START,
  };
  const forAllAcceptWithOutStartWithDotAndSpaceError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid format",
  };
  const onlyAlphaNumericWithoutDotValidations: FormFieldValidation = {
    pattern: ALPHA_NUMERIC_WITHOUT_DOT,
  };
  const onlyAlphaNumericWithoutDotErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format ",
  };

  // Latitude Validations

  const latitudesValidations: FormFieldValidation = {
    pattern: LAT_REGEX,
  };
  const latitudesValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Latitude",
  };
  // Longitude Validations

  const longitudesValidations: FormFieldValidation = {
    pattern: LONG_REGEX,
  };
  const longitudesValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Longitude",
  };

  // Only ALphabets
  const onlyAlphabetsValidations: FormFieldValidation = {
    pattern: ONLY_ALPHABETS,
  };
  const onlyAlphabetsValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only alphabets",
  };

  // Mobile Validations
  const mobileValidations: FormFieldValidation = { pattern: MOBILE_REGEX };
  const mobileValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Phone format.",
  };

  // Email Validations
  const emailValidations: FormFieldValidation = { pattern: EMAIL_REGEX };
  const emailValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid email format.",
  };

  // Name Validations
  const nameValidations: FormFieldValidation = { pattern: NAME_REGEX };
  const nameValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid name.",
  };

  // Indian Pan Number Validations
  const indianPanNumberValidations: FormFieldValidation = {
    pattern: INDIAN_PAN_NUMBER_REGEX,
  };

  const indianPanNumberErrors: IFormFieldErrorMessages = {
    message: "Invalid PAN number",
    type: "pattern",
  };

  // Numric with Decimal

  const onlyNumericWithDecimalValidations: FormFieldValidation = { pattern: NUMERIC_WITH_DECIMAL };
  const onlyNumericWithDecimalErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numeric values",
  };
  const onlyNumericWithDecimalAndNotStartFromZeroValidations: FormFieldValidation = {
    pattern: {
      value: NUMERIC_WITH_DECIMAL_AND_NOT_START_WITH_ZERO,
      message: "Invalid format",
    },
  };
  const onlyNumericWithDecimalAndNotStartFromZeroErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format",
  };
  const onlyTaxWithDecimalValidations: FormFieldValidation = { pattern: TAX_REGEX };
  const onlyTaxWithDecimalValidationsErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Only numeric with decimal value should not greater than 100",
  };
  const onlyPercentageValidation: FormFieldValidation = { pattern: PERCENTAGE_REGEX };
  const onlyPercentageValidationError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format",
  };

  const alphaNumericValidations: FormFieldValidation = { pattern: ALPHA_NUMERIC_REGEX };
  const alphaNumericValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format",
  };
  const alphaNumericWithGSTValidations: FormFieldValidation = { pattern: GST_REGEX };
  const alphaNumericWithGSTValidationsError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid GST Format",
  };

  const ratingValidations: FormFieldValidation = { pattern: RATING_REGEX };
  const ratingValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid name.",
  };

  const ratingScoreValidations: FormFieldValidation = { pattern: RATING_SCORE_REGEX };
  const ratingScoreValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Rating must be a single number between 0 and 5",
  };

  const staffNameValidations: FormFieldValidation = { pattern: STAFF_NAME_REGEX };
  const staffNameValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid name.",
  };
  const msmeValidationValidation: FormFieldValidation = { pattern: MSME_REGEX };
  const msmeValidationValidationError: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid msme number try e.g UDYAM-AB-12-1234567",
  };

  const equipmentNameValidations: FormFieldValidation = { pattern: EQUIPMENT_NAME_REGEX };
  const equipmentNameValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid name.",
  };
  const forOnlyAlphabetWithoutDotValidation: FormFieldValidation = { pattern: ONLY_ALPHABETS_WITH_OUT_DOT };
  const forOnlyAlphabetWithoutDotErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid name.",
  };
  const forOnlyAlphabetWithAlfhanumeric: FormFieldValidation = { pattern: ADDRESS_REGEX };
  const forOnlyAlphabetWithAlfhanumericErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid Format.",
  };

  const urlValidations: FormFieldValidation = { pattern: URL_REGEX };
  const urlValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid URL",
  };

  const gmbPlaceIdValidations: FormFieldValidation = { pattern: GMB_PLACE_ID_REGEX };
  const gmbPlaceIdValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid GMB Place ID",
  };

  const locationPathValidations: FormFieldValidation = { pattern: LOCATION_PATH_REGEX };
  const locationPathValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Cannot start with , or . and cannot contain special characters like & or ^",
  };
  // Alias Code Validations (Clinic Code)
  const aliasCodeValidations: FormFieldValidation = { pattern: ALIAS_CODE_REGEX, maxLength: 5 };
  const aliasCodeValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Invalid URL",
  };

  // Extension Days (1 - 366)
  const extensionDaysValidations: FormFieldValidation = {
    pattern: {
      value: EXTENSION_DAYS_REGEX,
      message: "Enter a whole number between 1 and 366",
    },
    maxLength: 3,
  };
  const extensionDaysValidationErrors: IFormFieldErrorMessages = {
    type: "pattern",
    message: "Enter a whole number between 1 and 366",
  };

  return {
    forOnlyAlphabetWithoutDot: {
      validations: forOnlyAlphabetWithoutDotValidation,
      errors: forOnlyAlphabetWithoutDotErrors,
    },
    forOnlyAlphabet: {
      validations: onlyAlphabetsValidations,
      errors: onlyAlphabetsValidationErrors,
    },
    forOnlyAlphabetWithAlfhanumeric: {
      validations: forOnlyAlphabetWithAlfhanumeric,
      errors: forOnlyAlphabetWithAlfhanumericErrors,
    },
    forOnlyNumber: {
      validations: onlyNumbersValidations,
      errors: onlyNumbersValidationErrors,
    },
    forOnlyBarcode: {
      validations: onlyBarcodeValidations,
      errors: onlyBarcodeValidationErrors,
    },
    forOnlyNumberWithoutZero: {
      validations: onlyNumbersValidationsWithoutZero,
      errors: onlyNumbersValidationsWithoutZeroErrors,
    },
    foruserPersonaCreation: {
      validations: onlyUserPerSona,
      errors: onlyUserPerSonaError,
    },
    forLatitude: {
      validations: latitudesValidations,
      errors: latitudesValidationErrors,
    },
    forLongitude: {
      validations: longitudesValidations,
      errors: longitudesValidationErrors,
    },
    forAddressLine: {
      validations: addressLineValidations,
      errors: addressLineValidationErrors,
    },
    forClinicAddressLine: {
      validations: clinicAddressLineValidations,
      errors: clinicAddressLineValidationErrors,
    },
    forMobile: {
      validations: mobileValidations,
      errors: mobileValidationErrors,
    },
    forName: { validations: nameValidations, errors: nameValidationErrors },
    forAlphaNumeric: { validations: alphaNumericValidations, errors: alphaNumericValidationErrors },
    forMsmeValidationValidation: { validations: msmeValidationValidation, errors: msmeValidationValidationError },
    forAlphaNumericWithGST: {
      validations: alphaNumericWithGSTValidations,
      errors: alphaNumericWithGSTValidationsError,
    },
    forRating: { validations: ratingValidations, errors: ratingValidationErrors },
    forRatingScore: { validations: ratingScoreValidations, errors: ratingScoreValidationErrors },
    forStaffName: { validations: staffNameValidations, errors: staffNameValidationErrors },
    forEquipmentName: { validations: equipmentNameValidations, errors: equipmentNameValidationErrors },
    forEmail: { validations: emailValidations, errors: emailValidationErrors },
    forNumericWithDecimal: {
      validations: onlyNumericWithDecimalValidations,
      errors: onlyNumericWithDecimalErrors,
    },
    forTaxRegex: {
      validations: onlyTaxWithDecimalValidations,
      errors: onlyTaxWithDecimalValidationsErrors,
    },
    forOnlyPercentageAndNumberRegex: {
      validations: onlyPercentageValidation,
      errors: onlyPercentageValidationError,
    },
    forIndianPanNumber: {
      errors: indianPanNumberErrors,
      validations: indianPanNumberValidations,
    },
    forAlphaNumericWithoutSpace: {
      validations: onlyAlphaNumericWithoutSpaceValidations,
      errors: onlyAlphaNumericWithoutSpaceErrors,
    },
    forAllAcceptWithOutStartWithDotAndSpace: {
      validations: forAllAcceptWithOutStartWithDotAndSpaceValidation,
      errors: forAllAcceptWithOutStartWithDotAndSpaceError,
    },
    forAlphaNumericWithoutDot: {
      validations: onlyAlphaNumericWithoutDotValidations,
      errors: onlyAlphaNumericWithoutDotErrors,
    },
    forOnlyStrictAlphabets: {
      validations: onlyStrictAlphabetsAndNumberValidations,
      errors: onlyStrictAlphabetsAndNumberErrors,
    },
    forOnlyAlphabetAndSpaces: {
      validations: onlyAlphabetsAndSpacesValidations,
      errors: onlyAlphabetsAndSpacesValidationErrors,
    },
    forOnlyNumbers: {
      validations: onlyNumbersValidationsNew,
      errors: onlyNumbersValidationErrorsNew,
    },
    forOnlyNumbersWithDecimal: {
      validations: onlyNumbersValidationsWithDecimal,
      errors: onlyNumbersValidationsWithDecimalError,
    },
    forOnlyAlphabetWithoutDotSpace: {
      validations: forOnlyAlphabetWithoutDotValidationNew,
      errors: forOnlyAlphabetWithoutDotErrorsNew,
    },
    forNumericWithDecimalAndNotStartFromZero: {
      validations: onlyNumericWithDecimalAndNotStartFromZeroValidations,
      errors: onlyNumericWithDecimalAndNotStartFromZeroErrors,
    },
    forUrl: {
      validations: urlValidations,
      errors: urlValidationErrors,
    },
    forGmbPlaceId: {
      validations: gmbPlaceIdValidations,
      errors: gmbPlaceIdValidationErrors,
    },
    forLocationPath: {
      validations: locationPathValidations,
      errors: locationPathValidationErrors,
    },
    forExtensionDays: {
      validations: extensionDaysValidations,
      errors: extensionDaysValidationErrors,
    },
    forAliasCode: {
      validations: aliasCodeValidations,
      errors: aliasCodeValidationErrors,
    },
  };
};

export default useFormValidations;
